import {Zap, PieChart, ArrowRight} from 'lucide-react'

export default function TDEEFatSection() {
  return (
    <section id="tdee-fat" className="relative py-16 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 mb-4">
            <Zap className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
              TDEE &amp; Fat
            </span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            How to Use Your TDEE to Calculate Fat Intake
          </h2>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Paragraphs */}
          <div className="lg:col-span-3 space-y-5">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Your <strong>Total Daily Energy Expenditure (TDEE)</strong> represents the total number of
              calories your body burns in a full day — including everything from breathing and digestion
              to walking, exercising, and even thinking. Unlike static calorie charts that assign the
              same number to everyone, TDEE is personalized to <em>your</em> body weight, height, age,
              gender, and activity level. That is what makes it the most reliable foundation for
              calculating how many grams of fat you should eat each day.
            </p>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Health authorities — including the Dietary Guidelines for Americans and the World Health
              Organization — recommend that <strong>20 % to 35 % of your total daily calories come from
              fat</strong>. When you apply those percentages directly to your TDEE instead of a generic
              2,000-calorie estimate, the result is far more accurate. For example, a highly active
              individual with a TDEE of 2,800 kcal needs 62–109 g of fat per day, while a sedentary
              person burning 1,800 kcal needs only 40–70 g. Using a one-size-fits-all number would
              over- or under-estimate fat needs by a significant margin.
            </p>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              This is precisely why our <strong>fat intake calculator</strong> starts by computing your
              TDEE through the Mifflin-St Jeor equation — the gold standard in clinical nutrition
              research. Once TDEE is established, the calculator applies your chosen fitness goal
              (weight loss, maintenance, or muscle gain) to adjust total calories, then derives the
              optimal fat range in both calories and grams. The result is a science-backed, personalized
              recommendation that adapts to your life — not a rough guess from a static table.
            </p>
          </div>

          {/* Visual sidebar */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200/60 p-6 sm:p-8">
              <h3
                className="text-lg font-bold text-slate-800 mb-5"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                TDEE-Based Fat Formula
              </h3>

              <div className="space-y-4">
                {[
                  {
                    icon: Zap,
                    step: "1",
                    title: "Find Your TDEE",
                    desc: "BMR × Activity Multiplier = Total calories burned per day.",
                  },
                  {
                    icon: PieChart,
                    step: "2",
                    title: "Apply Fat %",
                    desc: "Multiply TDEE by 0.20 and 0.35 to get the healthy calorie range from fat.",
                  },
                  {
                    icon: ArrowRight,
                    step: "3",
                    title: "Convert to Grams",
                    desc: "Divide fat calories by 9 (1 g fat = 9 kcal) to get your daily gram target.",
                  },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <div key={item.step} className="flex gap-4 items-start">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800 mb-0.5">{item.title}</p>
                        <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 pt-5 border-t border-emerald-200/60">
                <p className="text-xs text-slate-500 leading-relaxed">
                  <strong>Example:</strong> A moderately active 30-year-old male with a TDEE of 2,500 kcal
                  should consume <strong>56–97 g of fat</strong> daily (500–875 kcal from fat).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
